import { addSerialNumberToCsv } from "../lib/csv";
import { formatDateValue } from "../lib/dateFormat";
import DateInput from "../components/DateInput";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";

const numFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const C = { delivered: "#2563eb", shipped: "#d97706", pending: "#94a3b8" };
const LABEL = { delivered: "Delivered", shipped: "Shipped, awaiting delivery", pending: "Not yet shipped" };
const CHIP = {
  delivered: "border-blue-200 bg-blue-50 text-blue-800",
  shipped: "border-amber-200 bg-amber-50 text-amber-800",
  pending: "border-slate-200 bg-slate-50 text-slate-600",
};

function csvEscape(v) { if (v === null || v === undefined) return ""; const s = String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; }

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold" style={{ color: accent || "#0f172a" }}>{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

export default function OrderDeliveryStatusDetail() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [options, setOptions] = useState({ consignees: [], countries: [] });
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [consignee, setConsignee] = useState("");
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState("");
  const [dispatch, setDispatch] = useState("");
  const [payment, setPayment] = useState("");
  const [search, setSearch] = useState("");
  // Guards against an earlier request resolving after a newer one.
  const summarySeq = useRef(0);
  const detailSeq = useRef(0);

  useEffect(() => {
    api.get("/ceo/filter-options")
      .then((r) => setOptions({ consignees: r.data?.consignees || [], countries: r.data?.countries || [] }))
      .catch(() => setOptions({ consignees: [], countries: [] }));
  }, []);

  const body = useMemo(() => ({ from_date: fromDate, to_date: toDate, consignee, country, status, dispatch, payment }),
    [fromDate, toDate, consignee, country, status, dispatch, payment]);

  const load = useCallback(async () => {
    const seq = ++summarySeq.current; setLoading(true); setError("");
    try { const r = await api.post("/ai/ceo-chat/order-delivery-status", body); if (seq !== summarySeq.current) return; setSummary(r.data); }
    catch (err) { if (seq !== summarySeq.current) return; setError(formatErr(err)); }
    finally { if (seq === summarySeq.current) setLoading(false); }
  }, [body]);
  const loadDetail = useCallback(async () => {
    const seq = ++detailSeq.current; setDetailLoading(true); setDetailError("");
    try { const r = await api.post("/ai/ceo-chat/order-delivery-status/detail", { ...body, limit: 2000 }); if (seq !== detailSeq.current) return; setDetail(r.data); }
    catch (err) { if (seq !== detailSeq.current) return; setDetailError(formatErr(err)); }
    finally { if (seq === detailSeq.current) setDetailLoading(false); }
  }, [body]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const t = summary?.totals || {};
  const byCon = useMemo(() => summary?.by_consignee || [], [summary]);
  const maxCon = useMemo(() => Math.max(0, ...byCon.map((c) => c.pos)), [byCon]);
  const months = useMemo(() => (summary?.delivered_by_month || []).slice(-12), [summary]);
  const maxMonth = useMemo(() => Math.max(0, ...months.map((m) => m.delivered)), [months]);

  const filteredDetail = useMemo(() => {
    const list = detail?.rows || []; const q = search.trim().toLowerCase(); if (!q) return list;
    return list.filter((r) => [r.po_no, r.consignee, r.buyer, r.country, r.po_date].join(" ").toLowerCase().includes(q));
  }, [detail, search]);

  const exportCsv = useCallback(() => {
    const head = ["Order No", "Order Date", "Consignee", "Buyer", "Country", "Status", "Shipped Date", "Delivered (container returned)", "Days to Deliver", "FDA Exam", "Ordered Cartons", "Dispatch Date", "Payment"];
    const lines = [head.join(",")].concat(filteredDetail.map((r) => [r.po_no, r.po_date, r.consignee, r.buyer, r.country, r.status_label, r.shipped_date, r.delivered_date, r.days_to_deliver ?? "", r.fda_exam, r.ordered_mcs, r.dispatch_date, r.payment].map(csvEscape).join(",")));
    const url = URL.createObjectURL(new Blob([addSerialNumberToCsv(lines.join("\n"))], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = "order-delivery-status.csv"; a.click(); URL.revokeObjectURL(url);
  }, [filteredDetail]);

  const selectCls = "mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800";
  const labelCls = "text-xs font-semibold uppercase tracking-wide text-slate-500";

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1500px] space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="max-w-3xl">
            <h1 className="text-xl font-semibold text-slate-900">Delivered Orders</h1>
            <p className="mt-1 text-sm text-slate-600">
              Which orders have reached the customer. An order counts as delivered once it has shipped and the
              empty container has come back — the same rule the ERP uses on its orders-by-buyer screen. Orders that
              have shipped but whose container is still out are shown as awaiting delivery.
            </p>
          </div>
          <button type="button" onClick={() => navigate("/ceo-command-center?tab=shipment")}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Back to Shipments</button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className={labelCls}>From Date<DateInput value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={selectCls} /></label>
            <label className={labelCls}>To Date<DateInput value={toDate} onChange={(e) => setToDate(e.target.value)} className={selectCls} /></label>
            <label className={labelCls}>Consignee
              <select value={consignee} onChange={(e) => setConsignee(e.target.value)} className={`${selectCls} max-w-[220px]`}>
                <option value="">All Consignees</option>{options.consignees.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
            <label className={labelCls}>Country
              <select value={country} onChange={(e) => setCountry(e.target.value)} className={`${selectCls} max-w-[180px]`}>
                <option value="">All Countries</option>{options.countries.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
            <label className={labelCls}>Status
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
                <option value="">All Orders</option><option value="delivered">Delivered</option>
                <option value="shipped">Shipped, awaiting delivery</option><option value="pending">Not yet shipped</option></select></label>
            <label className={labelCls}>Dispatch
              <select value={dispatch} onChange={(e) => setDispatch(e.target.value)} className={selectCls}>
                <option value="">All</option><option value="dispatched">Dispatched (cold-store shipment out)</option>
                <option value="not_dispatched">Not dispatched</option></select></label>
            <label className={labelCls}>Payment
              <select value={payment} onChange={(e) => setPayment(e.target.value)} className={selectCls}>
                <option value="">All</option><option value="pending">Payments Pending</option>
                <option value="received">Payments Received</option></select></label>
            <button type="button" onClick={() => { setFromDate(""); setToDate(""); setConsignee(""); setCountry(""); setStatus(""); setDispatch(""); setPayment(""); }}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Reset Filters</button>
          </div>
          <div className="mt-2 text-xs text-slate-500">
                        {!fromDate && !toDate ? "No date filter applied — the full order history is shown." : ""}{" "}
            The headline split always covers every order in scope, so the three figures add up even when the Status filter is set.
          </div>
        </div>

        {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{error}</div> : null}
        {loading ? <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading…</div> : null}

        {!loading && summary ? (<>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <StatTile label="Orders in Scope" value={numFmt.format(t.total_pos || 0)} sub="Live orders matching the filters" />
            <StatTile label="Delivered" value={numFmt.format(t.delivered || 0)} sub="Shipped and container returned" accent={C.delivered} />
            <StatTile label="Awaiting Delivery" value={numFmt.format(t.shipped || 0)} sub="Shipped, container still out" accent={C.shipped} />
            <StatTile label="Not Yet Shipped" value={numFmt.format(t.pending || 0)} sub="No stock-out recorded" />
            <StatTile label="Dispatched" value={numFmt.format(t.dispatched || 0)} sub={`${numFmt.format(t.not_dispatched || 0)} not dispatched`} />
            <StatTile label="Payments Pending" value={numFmt.format(t.payment_pending || 0)} sub={`${numFmt.format(t.payment_received || 0)} received`} />
            <StatTile label="Avg Days to Deliver" value={t.avg_days_to_deliver != null ? `${t.avg_days_to_deliver}` : "—"} sub="Ship date to container return" />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">Delivery status by consignee</div>
              <div className="mt-1 text-xs text-slate-500">Top consignees by order count. Blue delivered · amber awaiting · grey not shipped.</div>
              <div className="mt-4 space-y-2">
                {byCon.map((c) => (
                  <div key={c.consignee} className="flex items-center gap-3">
                    <div className="w-44 shrink-0 truncate text-xs text-slate-700" title={c.consignee}>{c.consignee}</div>
                    <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                      {["delivered", "shipped", "pending"].map((k) => (
                        <div key={k} style={{ width: `${maxCon ? (c[k] / maxCon) * 100 : 0}%`, backgroundColor: C[k] }} />
                      ))}
                    </div>
                    <div className="w-36 shrink-0 text-right text-xs text-slate-700">
                      <span className="font-semibold text-slate-900">{numFmt.format(c.delivered)}</span> of {numFmt.format(c.pos)} delivered
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">Orders delivered per month</div>
              <div className="mt-1 text-xs text-slate-500">By the month the empty container came back.</div>
              <div className="mt-4 space-y-2">
                {months.map((m) => (
                  <div key={m.month} className="flex items-center gap-3">
                    <div className="w-20 shrink-0 text-xs text-slate-700">{m.month}</div>
                    <div className="h-3 flex-1 rounded-full bg-slate-100">
                      <div className="h-3 rounded-full" style={{ width: `${maxMonth ? Math.max((m.delivered / maxMonth) * 100, 0.5) : 0}%`, backgroundColor: C.delivered }} />
                    </div>
                    <div className="w-14 shrink-0 text-right text-xs font-semibold text-slate-900">{numFmt.format(m.delivered)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Orders</div>
                <div className="mt-1 text-xs text-slate-500">
                  {detailLoading ? "Loading…" : detail?.truncated
                    ? `Showing ${numFmt.format(filteredDetail.length)} of ${numFmt.format(detail.matching_orders || 0)} orders — narrow the filters to see them all`
                    : `${numFmt.format(filteredDetail.length)} order(s)`}
                </div>
              </div>
              <div className="flex gap-2">
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order, consignee, country" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                <button type="button" onClick={exportCsv} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Export CSV</button>
              </div>
            </div>
            {detailLoading ? <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-6 text-center text-sm text-slate-500">Loading orders…</div> : null}
            {detailError ? <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">The order list could not be loaded: {detailError} The figures above are unaffected.</div> : null}
            <div className={`mt-3 overflow-x-auto${detailLoading || detailError ? " hidden" : ""}`}>
              <table className="w-full text-left text-sm">
                <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3">Order No</th><th className="py-2 pr-3">Order Date</th><th className="py-2 pr-3">Consignee</th>
                  <th className="py-2 pr-3">Country</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Shipped</th>
                  <th className="py-2 pr-3">Delivered</th><th className="py-2 pr-3 text-right">Days</th><th className="py-2 pr-3 text-right">Cartons</th>
                  <th className="py-2 pr-3">Dispatch</th><th className="py-2 pr-3">Payment</th>
                </tr></thead>
                <tbody>{filteredDetail.slice(0, 300).map((r, i) => (
                  <tr key={`${r.po_id}-${i}`} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-medium text-slate-800">{r.po_no}</td>
                    <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{formatDateValue(r.po_date)}</td>
                    <td className="py-2 pr-3 text-slate-700">{r.consignee}</td>
                    <td className="py-2 pr-3 text-slate-600">{r.country}</td>
                    <td className="py-2 pr-3"><span className={`rounded-full border px-2 py-0.5 text-xs ${CHIP[r.status] || ""}`}>{LABEL[r.status] || r.status}</span></td>
                    <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{formatDateValue(r.shipped_date) || "—"}</td>
                    <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{formatDateValue(r.delivered_date) || "—"}</td>
                    <td className="py-2 pr-3 text-right text-slate-700">{r.days_to_deliver ?? "—"}</td>
                    <td className="py-2 pr-3 text-right text-slate-800">{numFmt.format(r.ordered_mcs)}</td>
                    <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{formatDateValue(r.dispatch_date) || "—"}</td>
                    <td className="py-2 pr-3"><span className={`rounded-full border px-2 py-0.5 text-xs ${r.payment === "received" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>{r.payment === "received" ? "Received" : "Pending"}</span></td>
                  </tr>))}</tbody>
              </table>
              {filteredDetail.length > 300 ? <div className="mt-2 text-xs text-slate-500">Showing the first 300 rows on screen. Export CSV includes all {numFmt.format(filteredDetail.length)}.</div> : null}
            </div>
          </div>

        </>) : null}
      </div>
    </div>
  );
}
